import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authAxios } from "../utils/auth";
import {
  ArrowLeft, RefreshCw, Users, TrendingUp, TrendingDown,
  UserPlus, Target, MailCheck, Wifi, AlertTriangle, Globe,
  FileText, ChevronDown, Activity, Zap, Mail, Route, Grid3x3, Settings,
  DollarSign, Diamond, Plus, Trash2, Network, Heart, Shield, Brain
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid, ReferenceDot
} from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import EmailerTab from "../components/analytics/EmailerTab";
import JourneysTab from "../components/analytics/JourneysTab";
import RetentionTab from "../components/analytics/RetentionTab";
import AnomaliesTab from "../components/analytics/AnomaliesTab";
import SettingsTab from "../components/analytics/SettingsTab";
import HealthCheckTab from "../components/analytics/HealthCheckTab";
import AffiliatesTab from "../components/analytics/AffiliatesTab";
import GuardianTab from "../components/analytics/GuardianTab";
import GuardianCard from "../components/analytics/GuardianCard";
import RecallTab from "../components/analytics/RecallTab";
import TrafficTrendChart from "../components/analytics/TrafficTrendChart";
import SalesTab from "../components/analytics/SalesTab";
import OnlineNowModal from "../components/analytics/OnlineNowModal";
import CostByAgentChart from "../components/admin/CostByAgentChart";
import AdminMenu from "../components/AdminMenu";

const GOLD = "#C5A55A";
const GOLD_LIGHT = "#e8d5a3";
const GOLD_DIM = "#8B7355";
const CHART_COLORS = [GOLD, "#3d8b6b", "#6b3d5e", "#3d5e8b", "#8b6b3d", "#5e3d6b"];

const RANGE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "90d", label: "Last 90 Days" },
  { value: "1y", label: "Last Year" },
  { value: "total", label: "Total" },
];

function fmt(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 1000) return (n / 1000).toFixed(1) + "K";
  return String(n);
}
function fmtTime(s) {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

// Convert an ISO string to a <input type="datetime-local"> value (local tz)
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(local) {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

// Compact diamond used as ReferenceDot shape for admin marketing events
const AnalyticsDiamondMarker = ({ cx, cy, marker, onOpen, color = "#7dd3fc" }) => (
  <g
    transform={`translate(${cx},${(cy || 0) - 22})`}
    style={{ cursor: "pointer" }}
    onClick={() => onOpen(marker)}
    data-testid={`analytics-event-marker-${marker.event_id}`}
  >
    <title>{marker.title}</title>
    <polygon points="0,-8 8,0 0,8 -8,0" fill={color} stroke="#0a0a0f" strokeWidth={1.5} />
    <line x1={0} y1={8} x2={0} y2={20} stroke={color} strokeWidth={1} strokeDasharray="2 2" opacity={0.5} />
  </g>
);

// ─── Hero Metric Card ───────────────────────────────────────────
function MetricCard({ icon: Icon, label, value, change, changeLabel, isPercentage, testId, onClick }) {
  const isPositive = change > 0;
  const isNeutral = change === 0;
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;

  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-white/[0.06] bg-[#111113] p-5 flex flex-col gap-1 ${
        onClick ? "cursor-pointer hover:border-[#C5A55A]/40 hover:bg-[#141416] transition-colors" : ""
      }`}
      data-testid={testId}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="absolute top-0 right-0 w-24 h-24 bg-[#C5A55A]/[0.03] rounded-bl-full" />
      <div className="flex items-center gap-2 text-white/40 text-xs font-medium uppercase tracking-wider mb-1">
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>
      <p className="text-3xl font-bold text-white tracking-tight">
        {isPercentage ? `${value}%` : fmt(value)}
      </p>
      {changeLabel !== undefined && (
        <div className={`flex items-center gap-1 text-xs mt-1 ${
          isNeutral ? "text-white/30" : isPositive ? "text-emerald-400" : "text-red-400"
        }`}>
          {!isNeutral && <TrendIcon className="w-3 h-3" />}
          <span>
            {isNeutral ? "No change" : `${isPositive ? "+" : ""}${change}${isPercentage ? "pp" : "%"} vs yesterday`}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Custom Tooltip ─────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1e] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-white/60 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-white font-medium">
          <span style={{ color: p.color || GOLD }}>{p.name}: </span>
          {p.value}
        </p>
      ))}
    </div>
  );
}

export default function AnalyticsCommandCenter() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "dashboard");
  // Keep the active tab in sync when the URL changes (e.g. GuardianCard
  // click on the Dashboard fires `navigate(?tab=guardian)` without a full
  // page reload).
  useEffect(() => {
    const t = searchParams.get("tab") || "dashboard";
    if (t !== activeTab) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [showOnlineNow, setShowOnlineNow] = useState(false);
  const [range, setRange] = useState("7d");
  const [hero, setHero] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [sources, setSources] = useState(null);
  const [errors, setErrors] = useState(null);
  const [pages, setPages] = useState(null);
  const [geo, setGeo] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [salesTimeline, setSalesTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  // Auto-refresh is OFF by default — Command Center is now click-on-demand
  // (Refresh button + range-change trigger a full batched fetch). Users
  // who WANT a background tick can toggle the green refresh icon in the
  // header to opt in to the 60s poll.
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [trendLines, setTrendLines] = useState({
    visitors: true, views: true, signups: true, sales: true,
    system_events: true, affiliate_events: true,
  });
  const [errorPage, setErrorPage] = useState(1);
  const [errorCodes, setErrorCodes] = useState({ 500: true, 403: true, 429: true, 422: true });
  const [showErrorInfo, setShowErrorInfo] = useState(false);

  // Marketing timeline events overlaid on Traffic Trend
  const [analyticsEvents, setAnalyticsEvents] = useState([]);   // admin-created "System" events
  const [affiliateEvents, setAffiliateEvents] = useState([]);   // per-user affiliate events
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formTitle, setFormTitle] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formDateTime, setFormDateTime] = useState("");
  const [savingEvent, setSavingEvent] = useState(false);

  // Affiliate-event view modal state (read-only + network stats + filter)
  const [affiliateModalOpen, setAffiliateModalOpen] = useState(false);
  const [viewingAffiliateEvent, setViewingAffiliateEvent] = useState(null);
  const [affiliateStats, setAffiliateStats] = useState(null);
  const [affiliateStatsLoading, setAffiliateStatsLoading] = useState(false);
  const [affiliateFilter, setAffiliateFilter] = useState(null); // {user_id, display_name}

  const openViewAffiliateEvent = useCallback(async (ev) => {
    setViewingAffiliateEvent(ev);
    setAffiliateStats(null);
    setAffiliateModalOpen(true);
    if (!ev?.user_id) return;
    setAffiliateStatsLoading(true);
    try {
      const { data } = await authAxios.get(`/analytics/command/affiliate-network-stats/${ev.user_id}`);
      setAffiliateStats(data);
    } catch (err) {
      console.error("affiliate stats fetch failed:", err);
      toast.error("Failed to load affiliate stats");
    } finally {
      setAffiliateStatsLoading(false);
    }
  }, []);

  const applyAffiliateFilter = () => {
    if (!viewingAffiliateEvent) return;
    setAffiliateFilter({
      user_id: viewingAffiliateEvent.user_id,
      display_name: viewingAffiliateEvent.affiliate_name || affiliateStats?.display_name || viewingAffiliateEvent.user_id,
    });
    setAffiliateModalOpen(false);
    toast.success(`Filtering by ${viewingAffiliateEvent.affiliate_name || "affiliate"}`);
  };

  const fetchAnalyticsEvents = useCallback(async () => {
    try {
      const [sys, aff] = await Promise.all([
        authAxios.get("/analytics/command/timeline-events"),
        authAxios.get("/analytics/command/affiliate-timeline-events").catch(() => ({ data: { events: [] } })),
      ]);
      setAnalyticsEvents(sys.data?.events || []);
      setAffiliateEvents(aff.data?.events || []);
    } catch (err) {
      console.error("analytics events fetch failed:", err);
    }
  }, []);

  useEffect(() => { fetchAnalyticsEvents(); }, [fetchAnalyticsEvents]);

  const openAddAnalyticsEvent = () => {
    setEditingEvent(null);
    setFormTitle("");
    setFormDesc("");
    setFormDateTime(toLocalInput(new Date().toISOString()));
    setEventModalOpen(true);
  };
  const openViewAnalyticsEvent = (ev) => {
    setEditingEvent(ev);
    setFormTitle(ev.title || "");
    setFormDesc(ev.description || "");
    setFormDateTime(toLocalInput(ev.event_at));
    setEventModalOpen(true);
  };
  const saveAnalyticsEvent = async () => {
    if (!formTitle.trim()) { toast.error("Title is required"); return; }
    setSavingEvent(true);
    try {
      if (editingEvent) {
        await authAxios.put(`/analytics/command/timeline-events/${editingEvent.event_id}`, {
          title: formTitle, description: formDesc,
          event_at: fromLocalInput(formDateTime),
        });
        toast.success("Event updated");
      } else {
        await authAxios.post(`/analytics/command/timeline-events`, {
          title: formTitle, description: formDesc,
          event_at: fromLocalInput(formDateTime),
        });
        toast.success("Event added");
      }
      setEventModalOpen(false);
      fetchAnalyticsEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save event");
    } finally { setSavingEvent(false); }
  };
  const deleteAnalyticsEvent = async () => {
    if (!editingEvent) return;
    if (!window.confirm(`Delete "${editingEvent.title}"?`)) return;
    setSavingEvent(true);
    try {
      await authAxios.delete(`/analytics/command/timeline-events/${editingEvent.event_id}`);
      toast.success("Event deleted");
      setEventModalOpen(false);
      fetchAnalyticsEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally { setSavingEvent(false); }
  };
  const intervalRef = useRef(null);

  // Check admin access
  useEffect(() => {
    authAxios.get("/auth/me").then(res => {
      if (res.data.role !== "admin") navigate("/dashboard", { replace: true });
    }).catch((err) => {
      // Only bounce to /auth on genuine auth failure. Network hiccups,
      // 5xx, or 429s must not kill the session — the refresh interceptor
      // has already tried and failed, so a real 401 here is the signal.
      const status = err?.response?.status;
      if (status === 401) navigate("/auth", { replace: true });
      else console.warn("Admin gate check non-fatal error:", status, err?.message);
    });
  }, [navigate]);

  const fetchHero = useCallback(async () => {
    try {
      const { data } = await authAxios.get("/analytics/command/hero");
      setHero(data);
    } catch (e) { console.error("Hero fetch error", e); }
  }, []);

  const buildErrorQuery = useCallback(() => {
    const activeCodes = Object.entries(errorCodes).filter(([, v]) => v).map(([k]) => k);
    const codesParam = activeCodes.length ? `&codes=${activeCodes.join(",")}` : "";
    return `page=${errorPage}&per_page=15${codesParam}`;
  }, [errorCodes, errorPage]);

  const fetchErrors = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`/analytics/command/errors?${buildErrorQuery()}`);
      setErrors(data);
    } catch (e) { console.error("Error fetch error", e); }
  }, [buildErrorQuery]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Note: we compute the errors query inline here (rather than using
      // buildErrorQuery from state) so that changing the errors filter or
      // page does NOT cause the entire 7-endpoint batch to refire —
      // errors have their own dedicated refetch effect below.
      const activeCodes = Object.entries(errorCodes).filter(([, v]) => v).map(([k]) => k);
      const codesParam = activeCodes.length ? `&codes=${activeCodes.join(",")}` : "";
      const errorsQuery = `page=1&per_page=15${codesParam}`;
      const [funnelRes, errorsRes, sourcesRes, pagesRes, geoRes, timelineRes, salesTlRes] = await Promise.all([
        authAxios.get(`/analytics/command/funnel?range=${range}`),
        authAxios.get(`/analytics/command/errors?${errorsQuery}`),
        authAxios.get(`/analytics/command/sources?range=${range}`),
        authAxios.get(`/analytics/command/pages?range=${range}`),
        authAxios.get(`/analytics/command/geo?range=${range}`),
        authAxios.get(`/analytics/command/timeline?range=${range}`),
        authAxios.get(`/analytics/sales/timeline?range=${range}`),
      ]);
      setFunnel(funnelRes.data);
      setErrors(errorsRes.data);
      setSources(sourcesRes.data);
      setPages(pagesRes.data);
      setGeo(geoRes.data);
      setTimeline(timelineRes.data);
      setSalesTimeline(salesTlRes.data);
      setLastUpdated(new Date());
    } catch (e) { console.error("Fetch error", e); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  // Initial load + range change
  useEffect(() => {
    fetchHero();
    fetchAll();
  }, [fetchHero, fetchAll]);

  // Re-fetch errors when filters or page change
  useEffect(() => {
    fetchErrors();
  }, [fetchErrors]);

  // Auto-refresh hero + errors every 60s
  useEffect(() => {
    if (!autoRefresh) { clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(() => {
      fetchHero();
      fetchErrors();
      setLastUpdated(new Date());
    }, 60000);
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, fetchHero]);

  const maxFunnel = funnel?.stages?.[0]?.count || 1;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="analytics-command-center">
      {/* ── Header ─────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-md border-b border-white/[0.06]">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin")} className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors" data-testid="back-to-admin">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold tracking-tight" style={{ color: GOLD }}>EROS COMMAND CENTER</h1>
              <p className="text-[11px] text-white/30 hidden sm:block">Real-Time Platform Intelligence</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-[11px] text-white/25 hidden md:inline">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="bg-[#111113] border border-white/10 text-white/80 text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#C5A55A]/50"
              data-testid="date-range-selector"
            >
              {RANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`p-2 rounded-lg transition-colors ${autoRefresh ? "text-emerald-400 bg-emerald-400/10" : "text-white/30 hover:text-white/60"}`}
              title={autoRefresh ? "Auto-refresh ON (60s) — click to switch to on-demand" : "Auto-refresh OFF — click to enable 60s tick"}
              data-testid="auto-refresh-toggle"
            >
              <RefreshCw className={`w-4 h-4 ${autoRefresh ? "animate-spin" : ""}`} style={autoRefresh ? { animationDuration: "8s" } : {}} />
            </button>
            <AdminMenu />
          </div>
        </div>
        {/* ── Tabs ── */}
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto py-1 -mb-px">
          {[
            { id: "dashboard", label: "Dashboard", icon: Activity },
            { id: "sales", label: "Sales", icon: DollarSign },
            { id: "emailer", label: "eMailer", icon: Mail },
            { id: "affiliates", label: "Affiliates", icon: Users },
            { id: "guardian", label: "Guardian", icon: Shield },
            { id: "journeys", label: "Journeys", icon: Route },
            { id: "retention", label: "Retention", icon: Grid3x3 },
            { id: "anomalies", label: "Anomalies", icon: AlertTriangle },
            { id: "recall", label: "Recall", icon: Brain },
            { id: "llm-costs", label: "LLM Costs", icon: DollarSign },
            { id: "health", label: "Health", icon: Heart },
            { id: "settings", label: "Settings", icon: Settings },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id); setSearchParams({ tab: tab.id });
              }}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? "text-[#C5A55A] bg-[#C5A55A]/10 border-b-2 border-[#C5A55A]"
                  : "text-white/30 hover:text-white/60 hover:bg-white/[0.03]"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* ── Hero Metrics (Dashboard tab only) ── */}
        {activeTab === "dashboard" && (
        <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="hero-metrics">
          {hero ? (
            <>
              <MetricCard icon={Wifi} label="Online Now" value={hero.online_now} testId="metric-online" onClick={() => setShowOnlineNow(true)} />
              <MetricCard icon={Users} label="Visitors Today" value={hero.visitors.today} change={hero.visitors.change} changeLabel={hero.visitors.change} testId="metric-visitors" />
              <MetricCard icon={UserPlus} label="Signups Today" value={hero.signups.today} change={hero.signups.change} changeLabel={hero.signups.change} testId="metric-signups" />
              <MetricCard icon={Target} label="Conversion Rate" value={hero.conversion_rate.today} change={hero.conversion_rate.change} changeLabel={hero.conversion_rate.change} isPercentage testId="metric-conversion" />
              <MetricCard icon={MailCheck} label="Emails Verified" value={hero.emails_verified.today} change={hero.emails_verified.change} changeLabel={hero.emails_verified.change} testId="metric-verified" />
            </>
          ) : (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-white/[0.06] bg-[#111113] p-5 h-28 animate-pulse" />
            ))
          )}
        </section>
        )}

        {/* ── Guardian summary tile (Dashboard tab only) ── */}
        {activeTab === "dashboard" && (
          <section data-testid="guardian-card-section">
            <GuardianCard />
          </section>
        )}

        {/* ── Traffic Trend (Dashboard tab only; shared component — same on /admin/sales) ── */}
        {activeTab === "dashboard" && (timeline?.timeline?.length > 0) && (
          <TrafficTrendChart timeline={timeline} salesTimeline={salesTimeline} />
        )}

        {/* ── Funnel + Sources ───────────────────────────── */}
        {activeTab === "dashboard" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Conversion Funnel */}
          <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="conversion-funnel">
            <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4" /> Conversion Funnel
            </h2>
            {funnel?.stages ? (
              <div className="space-y-3">
                {funnel.stages.map((stage, i) => {
                  const pct = maxFunnel > 0 ? (stage.count / maxFunnel) * 100 : 0;
                  return (
                    <div key={stage.name} data-testid={`funnel-stage-${i}`}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-white/70">{stage.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-white/90">{fmt(stage.count)}</span>
                          {i > 0 && stage.dropoff > 0 && (
                            <span className="text-red-400/80 text-[10px]">-{stage.dropoff}%</span>
                          )}
                        </div>
                      </div>
                      <div className="h-6 bg-white/[0.04] rounded-md overflow-hidden">
                        <div
                          className="h-full rounded-md transition-all duration-700 ease-out"
                          style={{
                            width: `${Math.max(pct, 2)}%`,
                            background: `linear-gradient(90deg, ${CHART_COLORS[i % CHART_COLORS.length]}, ${CHART_COLORS[i % CHART_COLORS.length]}88)`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-white/20">Loading...</div>
            )}
          </section>

          {/* Traffic Sources */}
          <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="traffic-sources">
            <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Globe className="w-4 h-4" /> Traffic Sources
            </h2>
            {sources?.categories?.length > 0 ? (
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="w-44 h-44 flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={sources.categories}
                        cx="50%" cy="50%"
                        innerRadius={45} outerRadius={70}
                        paddingAngle={2}
                        dataKey="count"
                        nameKey="name"
                      >
                        {sources.categories.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 w-full space-y-2">
                  {sources.categories.map((s, i) => (
                    <div key={s.name} className="flex items-center gap-3 text-sm">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span className="text-white/70 flex-1">{s.name}</span>
                      <span className="font-mono text-white/90">{fmt(s.count)}</span>
                      <span className="text-white/30 text-xs w-12 text-right">
                        {sources.total ? Math.round(s.count / sources.total * 100) : 0}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-white/20">
                {loading ? "Loading..." : "No traffic data"}
              </div>
            )}
          </section>
        </div>
        )}

        {/* ── Error Wall ─────────────────────────────────── */}
        {activeTab === "dashboard" && (
        <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="error-wall">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Error Wall
              <button
                onClick={() => setShowErrorInfo(true)}
                className="w-4 h-4 rounded-full border border-white/20 text-white/30 hover:text-white/60 hover:border-white/40 transition-colors flex items-center justify-center text-[9px] font-bold leading-none"
                data-testid="error-info-btn"
              >?</button>
            </h2>
            {errors && (
              <div className="flex items-center gap-2">
                {errors.daily_5xx > 0 && (
                  <span className="px-2 py-0.5 bg-red-500/15 text-red-400 text-xs rounded-full font-mono" data-testid="error-5xx-badge">
                    5xx: {errors.daily_5xx}
                  </span>
                )}
                {errors.daily_4xx > 0 && (
                  <span className="px-2 py-0.5 bg-amber-500/15 text-amber-400 text-xs rounded-full font-mono" data-testid="error-4xx-badge">
                    4xx: {errors.daily_4xx}
                  </span>
                )}
                <span className="px-2 py-0.5 bg-white/5 text-white/40 text-xs rounded-full font-mono" data-testid="error-total-badge">
                  Today: {errors.daily_total}
                </span>
              </div>
            )}
          </div>

          {/* Code filter checkboxes */}
          <div className="flex items-center gap-4 mb-4 pb-3 border-b border-white/[0.04]">
            <span className="text-[11px] text-white/25 uppercase tracking-wider">Filter</span>
            {[
              { code: 500, label: "500", color: "bg-red-500/20 text-red-400 border-red-500/30", desc: "Server Error" },
              { code: 403, label: "403", color: "bg-orange-500/20 text-orange-400 border-orange-500/30", desc: "Forbidden" },
              { code: 429, label: "429", color: "bg-amber-500/20 text-amber-400 border-amber-500/30", desc: "Rate Limit" },
              { code: 422, label: "422", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", desc: "Validation" },
            ].map(f => (
              <label key={f.code} className="flex items-center gap-1.5 cursor-pointer select-none group" data-testid={`error-filter-${f.code}`}>
                <input
                  type="checkbox"
                  checked={errorCodes[f.code]}
                  onChange={() => { setErrorCodes(prev => ({ ...prev, [f.code]: !prev[f.code] })); setErrorPage(1); }}
                  className="sr-only peer"
                />
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border transition-all ${
                  errorCodes[f.code] ? f.color : "bg-white/[0.02] text-white/15 border-white/[0.04]"
                }`}>{f.label}</span>
                <span className={`text-[10px] transition-colors ${errorCodes[f.code] ? "text-white/40" : "text-white/15"}`}>{f.desc}</span>
              </label>
            ))}
          </div>

          {errors?.errors?.length > 0 ? (
            <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-white/30 border-b border-white/[0.06]">
                    <th className="text-left py-2 px-2 font-medium">Time</th>
                    <th className="text-left py-2 px-2 font-medium">Code</th>
                    <th className="text-left py-2 px-2 font-medium">Method</th>
                    <th className="text-left py-2 px-2 font-medium">URL</th>
                    <th className="text-left py-2 px-2 font-medium">Referrer</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.errors.map((err, i) => {
                    const is5xx = err.status_code >= 500;
                    return (
                      <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02]" data-testid={`error-row-${i}`}>
                        <td className="py-2 px-2 text-white/50 whitespace-nowrap">
                          {err.timestamp ? new Date(err.timestamp).toLocaleTimeString() : "-"}
                        </td>
                        <td className="py-2 px-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                            is5xx ? "bg-red-500/20 text-red-400" : "bg-amber-500/20 text-amber-400"
                          }`}>
                            {err.status_code}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-white/40 font-mono">{err.method}</td>
                        <td className="py-2 px-2 text-white/70 font-mono max-w-[300px] truncate">{err.path}</td>
                        <td className="py-2 px-2 text-white/30 max-w-[200px] truncate">{err.referrer || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {errors.total_pages > 1 && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.04]">
                <span className="text-[11px] text-white/25">{errors.total} errors total</span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setErrorPage(p => Math.max(1, p - 1))}
                    disabled={errorPage <= 1}
                    className="px-2.5 py-1 text-[11px] rounded bg-white/[0.04] text-white/40 hover:bg-white/[0.08] hover:text-white/70 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                    data-testid="error-page-prev"
                  >Prev</button>
                  <span className="text-[11px] text-white/30 px-2">
                    {errorPage} / {errors.total_pages}
                  </span>
                  <button
                    onClick={() => setErrorPage(p => Math.min(errors.total_pages, p + 1))}
                    disabled={errorPage >= errors.total_pages}
                    className="px-2.5 py-1 text-[11px] rounded bg-white/[0.04] text-white/40 hover:bg-white/[0.08] hover:text-white/70 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                    data-testid="error-page-next"
                  >Next</button>
                </div>
              </div>
            )}
            </>
          ) : (
            <div className="h-24 flex items-center justify-center text-white/20 text-sm">
              {loading ? "Loading..." : "No errors recorded"}
            </div>
          )}
        </section>
        )}

        {/* Error Info Modal */}
        {showErrorInfo && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowErrorInfo(false)} data-testid="error-info-modal">
            <div className="bg-[#111113] border border-white/10 rounded-xl max-w-md w-full mx-4 p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-white/80">Error Wall — What am I looking at?</h3>
                <button onClick={() => setShowErrorInfo(false)} className="text-white/30 hover:text-white/60 text-lg leading-none">&times;</button>
              </div>
              <div className="space-y-3 text-xs text-white/60">
                <p>The Error Wall tracks meaningful HTTP errors from your API, excluding routine auth checks (401) and not-found noise (404).</p>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500/20 text-red-400 mt-0.5 flex-shrink-0">500</span>
                    <div><strong className="text-white/80">Server Error</strong> — Backend crash, unhandled exception, or database failure. Something is broken and needs immediate attention.</div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-500/20 text-orange-400 mt-0.5 flex-shrink-0">403</span>
                    <div><strong className="text-white/80">Forbidden</strong> — Someone tried to access a resource they don&apos;t have permission for (e.g. non-admin hitting admin routes).</div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-400 mt-0.5 flex-shrink-0">429</span>
                    <div><strong className="text-white/80">Rate Limited</strong> — Too many requests from a single user/IP. Could indicate abuse, bots, or aggressive polling.</div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-yellow-500/20 text-yellow-400 mt-0.5 flex-shrink-0">422</span>
                    <div><strong className="text-white/80">Validation Error</strong> — Malformed request data. Could indicate a frontend bug sending bad payloads.</div>
                  </div>
                </div>
                <p className="text-white/30 pt-2 border-t border-white/[0.06]">Filtered out: 401 (auth checks), 404 (not found). Use the checkboxes to toggle each error type.</p>
              </div>
            </div>
          </div>
        )}

        {/* ── Top Pages ──────────────────────────────────── */}
        {activeTab === "dashboard" && (
        <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="top-pages">
          <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Top Pages
          </h2>
          {pages?.pages?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-white/30 border-b border-white/[0.06]">
                    <th className="text-left py-2 px-2 font-medium w-8">#</th>
                    <th className="text-left py-2 px-2 font-medium">Page</th>
                    <th className="text-right py-2 px-2 font-medium">Views</th>
                    <th className="text-right py-2 px-2 font-medium">Unique</th>
                    <th className="text-right py-2 px-2 font-medium">Avg Time</th>
                    <th className="text-right py-2 px-2 font-medium">Bounce</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.pages.map((p, i) => (
                    <tr key={p.path} className="border-b border-white/[0.03] hover:bg-white/[0.02]" data-testid={`page-row-${i}`}>
                      <td className="py-2.5 px-2 text-white/25 font-mono">{i + 1}</td>
                      <td className="py-2.5 px-2 text-white/80 font-mono">{p.path}</td>
                      <td className="py-2.5 px-2 text-right text-white/70 font-mono">{fmt(p.views)}</td>
                      <td className="py-2.5 px-2 text-right text-white/50 font-mono">{fmt(p.unique_visitors)}</td>
                      <td className="py-2.5 px-2 text-right text-white/50 font-mono">{fmtTime(p.avg_time_seconds || 0)}</td>
                      <td className="py-2.5 px-2 text-right font-mono">
                        <span className={p.bounce_rate > 60 ? "text-red-400" : p.bounce_rate > 40 ? "text-amber-400" : "text-emerald-400"}>
                          {p.bounce_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-24 flex items-center justify-center text-white/20 text-sm">
              {loading ? "Loading..." : "No page data"}
            </div>
          )}
        </section>
        )}

        {/* ── Geographic Distribution ────────────────────── */}
        {activeTab === "dashboard" && (
        <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="geo-distribution">
          <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4" /> Geographic Distribution
          </h2>
          {geo?.countries?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
              {geo.countries.map((c, i) => (
                <div key={c.code} className="flex items-center gap-3 py-1.5" data-testid={`geo-row-${i}`}>
                  <span className="text-xs font-mono text-white/40 w-5">{i + 1}</span>
                  <span className="text-sm font-mono text-white/30 w-8">{c.code}</span>
                  <span className="text-sm text-white/70 flex-1 truncate">{c.country}</span>
                  <div className="w-24 h-2 bg-white/[0.04] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${c.percentage}%`,
                        background: GOLD,
                        minWidth: "4px",
                      }}
                    />
                  </div>
                  <span className="text-xs text-white/50 font-mono w-12 text-right">{c.percentage}%</span>
                  <span className="text-xs text-white/30 font-mono w-10 text-right">{fmt(c.count)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-24 flex items-center justify-center text-white/20 text-sm">
              {loading ? "Loading..." : "No geo data"}
            </div>
          )}
        </section>
        )}

        {/* ── Sales Tab (inline — same UI as /admin/sales) ── */}
        {activeTab === "sales" && <SalesTab range={range} />}

        {/* ── eMailer Tab ── */}
        {activeTab === "emailer" && <EmailerTab range={range} />}

        {/* ── Affiliates Tab ── */}
        {activeTab === "affiliates" && <AffiliatesTab range={range} />}

        {/* ── Guardian Tab ── */}
        {activeTab === "guardian" && <GuardianTab />}

        {/* ── Journeys Tab ── */}
        {activeTab === "journeys" && <JourneysTab range={range} />}

        {/* ── Retention Tab ── */}
        {activeTab === "retention" && <RetentionTab />}

        {/* ── Anomalies Tab ── */}
        {activeTab === "anomalies" && <AnomaliesTab />}

        {activeTab === "recall" && <RecallTab range={range} />}

        {/* ── LLM Costs Tab ── */}
        {activeTab === "llm-costs" && (
          <section data-testid="llm-costs-section">
            <CostByAgentChart />
          </section>
        )}

        {/* ── Health Check Tab ── */}
        {activeTab === "health" && <HealthCheckTab />}

        {/* ── Settings Tab ── */}
        {activeTab === "settings" && <SettingsTab />}
      </main>

      {/* Online Now modal — opens when the hero MetricCard is clicked */}
      <OnlineNowModal open={showOnlineNow} onClose={() => setShowOnlineNow(false)} />

      {/* Marketing-event modals (Add/Edit + Affiliate view) now live inside
          the shared <TrafficTrendChart /> component so they render exactly
          once regardless of how many pages embed the chart. */}
    </div>
  );
}
